"use client";

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { v4 as uuidv4 } from "uuid";

import {
  type SavedBrand,
  createSavedBrand as createSavedBrandAPI,
  deleteSavedBrand as deleteSavedBrandAPI,
  listSavedBrandsForUser,
  updateSavedBrand as updateSavedBrandAPI,
} from "@/lib/shop-agent-client";

import { useAuth } from "./AuthContext";

interface CreateSavedBrandInput {
  brandName: string;
  brandSlug?: string | null;
  brandUuid?: string | null;
  brandDescription?: string | null;
}

interface SavedBrandsContextType {
  savedBrands: SavedBrand[];
  loading: boolean;
  error: string | null;
  createSavedBrand: (input: CreateSavedBrandInput) => Promise<SavedBrand | null>;
  deleteSavedBrand: (id: string) => Promise<boolean>;
  updateSavedBrand: (id: string, updates: Partial<SavedBrand>) => Promise<boolean>;
  isBrandSaved: (brandName: string) => boolean;
  getSavedBrandByName: (brandName: string) => SavedBrand | null;
  fetchSavedBrands: () => Promise<void>;
}

const SavedBrandsContext = createContext<SavedBrandsContextType | undefined>(undefined);

const normalizeBrandName = (brandName: string): string =>
  brandName.trim().toLowerCase().replace(/\s+/g, " ");

export function SavedBrandsProvider({ children }: { children: React.ReactNode }) {
  const [savedBrands, setSavedBrands] = useState<SavedBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();

  const fetchSavedBrands = useCallback(async () => {
    if (!user || !isAuthenticated) {
      setSavedBrands([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      setSavedBrands(await listSavedBrandsForUser());
    } catch (err) {
      console.error("Failed to list saved brands:", err);
      setError("Failed to load saved brands");
      setSavedBrands([]);
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    fetchSavedBrands();
  }, [fetchSavedBrands]);

  const getSavedBrandByName = useCallback(
    (brandName: string): SavedBrand | null => {
      const normalized = normalizeBrandName(brandName);
      return savedBrands.find((brand) => brand.normalized_brand_name === normalized) ?? null;
    },
    [savedBrands]
  );

  const isBrandSaved = useCallback(
    (brandName: string): boolean => Boolean(getSavedBrandByName(brandName)),
    [getSavedBrandByName]
  );

  const createSavedBrand = async (input: CreateSavedBrandInput): Promise<SavedBrand | null> => {
    if (!user) return null;

    try {
      const savedBrand = await createSavedBrandAPI({
        id: uuidv4(),
        brand_name: input.brandName,
        brand_slug: input.brandSlug ?? undefined,
        brand_uuid: input.brandUuid ?? undefined,
        brand_description: input.brandDescription ?? undefined,
        is_public: false,
      });
      await fetchSavedBrands();
      return savedBrand;
    } catch (err) {
      console.error("Failed to create saved brand:", err);
      return null;
    }
  };

  const updateSavedBrand = async (id: string, updates: Partial<SavedBrand>): Promise<boolean> => {
    if (!user) return false;

    try {
      await updateSavedBrandAPI(id, {
        brand_name: updates.brand_name,
        brand_slug: updates.brand_slug,
        brand_uuid: updates.brand_uuid,
        brand_description: updates.brand_description,
        is_public: updates.is_public,
      });
      await fetchSavedBrands();
      return true;
    } catch (err) {
      console.error("Failed to update saved brand:", err);
      return false;
    }
  };

  const deleteSavedBrand = async (id: string): Promise<boolean> => {
    if (!user) return false;

    try {
      await deleteSavedBrandAPI(id);
      await fetchSavedBrands();
      return true;
    } catch (err) {
      console.error("Failed to delete saved brand:", err);
      return false;
    }
  };

  return (
    <SavedBrandsContext.Provider
      value={{
        savedBrands,
        loading,
        error,
        createSavedBrand,
        deleteSavedBrand,
        updateSavedBrand,
        isBrandSaved,
        getSavedBrandByName,
        fetchSavedBrands,
      }}
    >
      {children}
    </SavedBrandsContext.Provider>
  );
}

export const useSavedBrands = () => {
  const context = useContext(SavedBrandsContext);
  if (!context) {
    throw new Error("useSavedBrands must be used within a SavedBrandsProvider");
  }
  return context;
};
